/**
 * Apple In-App Purchases integration for iOS
 * 
 * Uses cordova-plugin-purchase (CdvPurchase) - FREE, open-source
 * https://github.com/j3k0/cordova-plugin-purchase
 * 
 * Install: npm install cordova-plugin-purchase && npx cap sync
 */

import { isNativePlatform, getPlatform } from './platform';
import { supabase } from '@/integrations/supabase/client';
import { logPaymentEvent } from './payment-analytics';

/**
 * Purchasable Apple product IDs (from App Store Connect).
 * Both are CONSUMABLE one-time credit packs — they can be bought repeatedly.
 */
export const APPLE_PRODUCT_IDS = {
  refill: 'DC_Onetime_Refill',
  boost: 'DC_Onetime_Boost',
} as const;

/**
 * Retired products. Not purchasable — kept only so existing buyers can be
 * recognised and restored.
 */
export const LEGACY_APPLE_PRODUCT_IDS = {
  proOneTime: 'DCProOneTime',
  pro: 'DClite',
  business: 'DCBuisness',
} as const;

/** Credits granted per pack — must match the store listings and the backend. */
export const APPLE_PACK_CREDITS: Record<string, number> = {
  [APPLE_PRODUCT_IDS.refill]: 20,
  [APPLE_PRODUCT_IDS.boost]: 50,
};

export type AppleProductTier = keyof typeof APPLE_PRODUCT_IDS;


export interface AppleProduct {
  id: string;
  title: string;
  description: string;
  price: string;
  priceAmount: number;
  currency: string;
}

export interface PurchaseResult {
  success: boolean;
  transactionId?: string;
  error?: string;
}

export interface VerificationResult {
  success: boolean;
  tier?: string;
  credits?: number;
  error?: string;
}

export interface RestoreResult {
  success: boolean;
  restoredProducts: string[];
  error?: string;
}

/**
 * Check if we should use Apple In-App Purchases
 */
export const shouldUseAppleIAP = (): boolean => {
  return isNativePlatform() && getPlatform() === 'ios';
};

/**
 * Get the CdvPurchase store instance
 */
function getStore(): any {
  if (typeof window !== 'undefined' && (window as any).CdvPurchase?.store) {
    return (window as any).CdvPurchase.store;
  }
  return null;
}

/**
 * Get CdvPurchase namespace
 */
function getCdvPurchase(): any {
  if (typeof window !== 'undefined' && (window as any).CdvPurchase) {
    return (window as any).CdvPurchase;
  }
  return null;
}

// Store initialization state
let isStoreInitialized = false;
let initializationPromise: Promise<boolean> | null = null;
const verificationPromises = new Map<string, Promise<VerificationResult>>();
const verifiedTransactions = new Map<string, VerificationResult>();

/**
 * Initialize the store - call this on app startup
 */
export const initializeAppleIAP = async (): Promise<boolean> => {
  if (!shouldUseAppleIAP()) {
    console.log('[Apple IAP] Not on iOS, skipping initialization');
    return false;
  }

  // Return existing promise if already initializing
  if (initializationPromise) {
    return initializationPromise;
  }

  initializationPromise = (async () => {
    try {
      const CdvPurchase = getCdvPurchase();
      const store = getStore();
      
      if (!store || !CdvPurchase) {
        console.error('[Apple IAP] cordova-plugin-purchase not available');
        return false;
      }

      console.log('[Apple IAP] Initializing cordova-plugin-purchase...');

      // Set debug verbosity in development
      store.verbosity = CdvPurchase.LogLevel.DEBUG;

      // Register the one-time credit packs as consumables so they can be
      // purchased repeatedly.
      store.register(Object.values(APPLE_PRODUCT_IDS).map((id: string) => ({
        id,
        type: CdvPurchase.ProductType.CONSUMABLE,
        platform: CdvPurchase.Platform.APPLE_APPSTORE,
      })));

      // Retired non-consumable product — registered only so existing owners
      // can restore it. Not offered for purchase.
      store.register([{
        id: LEGACY_APPLE_PRODUCT_IDS.proOneTime,
        type: CdvPurchase.ProductType.NON_CONSUMABLE,
        platform: CdvPurchase.Platform.APPLE_APPSTORE,
      }]);


      // Set up event handlers
      store.when()
        .approved((transaction: any) => {
          console.log('[Apple IAP] Transaction approved:', transaction.transactionId);
          // Verify with our backend then finish
          handleApprovedTransaction(transaction);
        })
        .verified((receipt: any) => {
          console.log('[Apple IAP] Receipt verified:', receipt);
          receipt.finish();
        })
        .finished((transaction: any) => {
          console.log('[Apple IAP] Transaction finished:', transaction.transactionId);
        });

      // Track errors
      store.error((error: any) => {
        if (error.code === CdvPurchase.ErrorCode.PAYMENT_CANCELLED) {
          console.log('[Apple IAP] User cancelled purchase');
          return;
        }
        console.error('[Apple IAP] Store error:', error);
      });

      // Initialize the store
      await store.initialize([CdvPurchase.Platform.APPLE_APPSTORE]);
      
      isStoreInitialized = true;
      console.log('[Apple IAP] Store initialized successfully');
      return true;
    } catch (error) {
      console.error('[Apple IAP] Initialization failed:', error);
      return false;
    }
  })();

  return initializationPromise;
};

/**
 * Handle approved transaction - verify with backend
 */
async function handleApprovedTransaction(transaction: any): Promise<void> {
  try {
    const transactionId = transaction.transactionId;
    if (transactionId) {
      const result = await verifyAppleTransaction(transactionId);
      if (result.success) {
        console.log('[Apple IAP] Backend verification successful');
        transaction.verify();
      }
    }
  } catch (error) {
    console.error('[Apple IAP] Error handling approved transaction:', error);
  }
}

/**
 * Fetch available products with localized pricing
 */
export const getAppleProducts = async (): Promise<AppleProduct[]> => {
  if (!shouldUseAppleIAP()) {
    return [];
  }

  try {
    // Ensure store is initialized
    if (!isStoreInitialized) {
      await initializeAppleIAP();
    }

    const store = getStore();
    if (!store) {
      return [];
    }

    console.log('[Apple IAP] Fetching products...');

    // Get products from store
    const products: AppleProduct[] = [];
    
    for (const productId of Object.values(APPLE_PRODUCT_IDS)) {
      const product = store.get(productId);
      if (product) {
        // Get the pricing from the first offer
        const offer = product.offers?.[0];
        const pricingPhase = offer?.pricingPhases?.[0];
        
        products.push({
          id: product.id,
          title: product.title || product.id,
          description: product.description || '',
          price: pricingPhase?.price || product.pricing?.price || '$0.00',
          priceAmount: pricingPhase?.priceMicros 
            ? pricingPhase.priceMicros / 1000000 
            : (product.pricing?.priceMicros ? product.pricing.priceMicros / 1000000 : 0),
          currency: pricingPhase?.currency || product.pricing?.currency || 'USD',
        });
      }
    }

    console.log('[Apple IAP] Found', products.length, 'products:', products);
    return products;
  } catch (error) {
    console.error('[Apple IAP] Failed to fetch products:', error);
    return [];
  }
};

/**
 * Purchase a one-time credit pack
 */
export const purchaseAppleProduct = async (productId: string): Promise<PurchaseResult> => {
  if (!shouldUseAppleIAP()) {
    return { success: false, error: 'Not on iOS' };
  }

  try {
    const store = getStore();
    if (!store) {
      return { success: false, error: 'Store not available' };
    }

    // Ensure store is initialized
    if (!isStoreInitialized) {
      await initializeAppleIAP();
    }

    console.log('[Apple IAP] Starting purchase for:', productId);
    logPaymentEvent('checkout_initiated', { provider: 'apple_iap', plan_id: productId });

    const product = store.get(productId);
    if (!product) {
      return { success: false, error: 'Product not found' };
    }

    // Get the first offer and order it
    const offer = product.getOffer();
    if (!offer) {
      return { success: false, error: 'No offer available' };
    }

    // Create a promise to track the purchase result
    return new Promise((resolve) => {
      const CdvPurchase = getCdvPurchase();
      
      // Set up one-time handlers for this purchase
      const cleanup = store.when()
        .approved((transaction: any) => {
          if (transaction.products.some((p: any) => p.id === productId)) {
            console.log('[Apple IAP] Purchase approved:', transaction.transactionId);
            logPaymentEvent('checkout_succeeded', {
              provider: 'apple_iap',
              plan_id: productId,
              metadata: { transaction_id: transaction.transactionId },
            });
            resolve({
              success: true,
              transactionId: transaction.transactionId,
            });
            cleanup?.unregister?.();
          }
        });

      // Also listen for errors
      const errorHandler = (error: any) => {
        if (error.code === CdvPurchase.ErrorCode.PAYMENT_CANCELLED) {
          logPaymentEvent('checkout_abandoned', { provider: 'apple_iap', plan_id: productId });
          resolve({ success: false, error: 'Purchase cancelled' });
        } else {
          logPaymentEvent('checkout_failed', {
            provider: 'apple_iap',
            plan_id: productId,
            metadata: { error: String(error?.message || error?.code || 'unknown') },
          });
          resolve({ success: false, error: error.message || 'Purchase failed' });
        }
        cleanup?.unregister?.();
      };
      
      // Start the purchase
      offer.order()
        .then((result: any) => {
          if (result?.isError) {
            resolve({ success: false, error: result.message || 'Order failed' });
            cleanup?.unregister?.();
          }
        })
        .catch((error: any) => {
          errorHandler(error);
        });

      // Timeout after 2 minutes
      setTimeout(() => {
        resolve({ success: false, error: 'Purchase timeout' });
        cleanup?.unregister?.();
      }, 120000);
    });
  } catch (error: any) {
    console.error('[Apple IAP] Purchase failed:', error);
    return { success: false, error: error.message || 'Purchase failed' };
  }
};

/**
 * Verify a transaction with our backend
 */
export const verifyAppleTransaction = async (transactionId: string): Promise<VerificationResult> => {
  const cached = verifiedTransactions.get(transactionId);
  if (cached) return cached;

  const inFlight = verificationPromises.get(transactionId);
  if (inFlight) {
    console.log('[Apple IAP] Reusing in-progress verification:', transactionId);
    return inFlight;
  }

  const verification = (async (): Promise<VerificationResult> => {
    try {
      console.log('[Apple IAP] Verifying transaction:', transactionId);

      const { data, error } = await supabase.functions.invoke('verify-apple-receipt', {
        body: { transactionId }
      });

      if (error) {
        console.error('[Apple IAP] Verification error:', error);
        return { success: false, error: error.message };
      }

      if (data?.success) {
        console.log('[Apple IAP] Verification successful:', data);
        const result: VerificationResult = {
          success: true,
          tier: data.tier,
          credits: data.credits
        };
        verifiedTransactions.set(transactionId, result);
        return result;
      }

      return { success: false, error: data?.error || 'Verification failed' };
    } catch (error: any) {
      console.error('[Apple IAP] Verification exception:', error);
      return { success: false, error: error.message || 'Verification failed' };
    } finally {
      verificationPromises.delete(transactionId);
    }
  })();

  verificationPromises.set(transactionId, verification);
  return verification;
};

/**
 * Restore previous purchases.
 *
 * Consumable credit packs are not restorable by design — spent credits stay
 * spent and the wallet balance already lives on the account. This only
 * restores the retired non-consumable entitlement for existing owners.
 */
export const restoreApplePurchases = async (): Promise<RestoreResult> => {
  if (!shouldUseAppleIAP()) {
    return { success: false, restoredProducts: [], error: 'Not on iOS' };
  }

  try {
    const store = getStore();
    if (!store) {
      return { success: false, restoredProducts: [], error: 'Store not available' };
    }

    console.log('[Apple IAP] Restoring purchases...');

    await store.restorePurchases();

    const consumableIds = new Set<string>(Object.values(APPLE_PRODUCT_IDS));
    const restoredProducts: string[] = [];
    for (const purchase of store.verifiedPurchases || []) {
      if (consumableIds.has(purchase.id)) continue; // consumables are not restorable
      restoredProducts.push(purchase.id);
      // Re-verify with our backend (idempotent — never credits twice)
      if (purchase.transactionId) {
        await verifyAppleTransaction(purchase.transactionId);
      }
    }

    console.log('[Apple IAP] Restored products:', restoredProducts);
    return { success: true, restoredProducts };
  } catch (error: any) {
    console.error('[Apple IAP] Restore failed:', error);
    return { success: false, restoredProducts: [], error: error.message || 'Restore failed' };
  }
};

/**
 * Get the Apple product ID for a credit pack
 */
export const getAppleProductId = (pack: AppleProductTier): string => {
  return APPLE_PRODUCT_IDS[pack];
};

/**
 * Map an Apple product ID to its pack name
 */
export const appleProductToPack = (productId: string): string | null => {
  for (const [pack, id] of Object.entries(APPLE_PRODUCT_IDS)) {
    if (id === productId) return pack;
  }
  return null;
};

